import { Suspense, useMemo } from 'react';
import { OneUserFavoritesResource } from '../resources/UserFavorites/One';
import useCopy from '../util/useCopy';
import { tmdbConfig } from '../config/constants';
import ObjectiveStateResource from '../resources/ObjectiveState';
import Loader from './Loader';
import Modal from './Modal';
import { Header } from './Header';
import Action from './Action';
import { toggleUserFavoriteMutation } from '../resources/UserFavorites/mutations';
import { CurrentUserMustResource } from '../resources/Auth/CurrentUserMust';
import ArrowUpRightIcon from './ArrowUpRightIcon';
import AllUserProfilesResource from '../resources/AllUserProfiles';
import { OneUserLetterboxdData } from '../resources/Letterboxd';
import { StarRating } from './StarRating';
import { HeartIcon } from './HeartIcon';
import { Tooltip } from './Tooltip';
import { FilmOverview } from './FilmOverview';

export let filmsLimit = 2;

function formatReleaseDate(dateStr: string | null): string {
  if (!dateStr) return '';
  let date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
}

let FavoritesModalContent = ({
    onClose,
    userId,
    userHandle,
    className,
  }: {
    userId: string;
    userHandle: string;
    className?: string;
    onClose: () => void;
  }) => {
    let copy = useCopy();
    let currentUser = CurrentUserMustResource.use();
    let objectiveState = ObjectiveStateResource.use();
    let [allEnums] = AllUserProfilesResource.useLoadable();
    let isCurrentUser = currentUser.id === userId;

    let letterboxdId = allEnums?.users[userId]?.letterboxdId;
    let letterboxdResult = OneUserLetterboxdData.use(letterboxdId || undefined);
    let oscarFilmsStatus =
      letterboxdResult?.status === 'ok'
        ? letterboxdResult.data.oscarFilmsStatus
        : null;

    let favorites = OneUserFavoritesResource.use(userId);
    let isEmpty = !favorites || !favorites.length;

    let nominationsByFilm = useMemo(() => {
      let map = new Map<string, string[]>();
      for (let [, category] of Object.entries(
        objectiveState.nomineeCategories,
      )) {
        for (let nominee of Object.values(category.nominees)) {
          let filmId = (nominee as { filmId: string }).filmId;
          if (!map.has(filmId)) {
            map.set(filmId, []);
          }
          map.get(filmId)!.push(category.title);
        }
      }
      return map;
    }, [objectiveState.nomineeCategories]);

    if (favorites === null || isEmpty) return null;

    return (
      <>
        <Header className="mb-4">
          {copy.getOtherUserFavoritesMessage({
            handle: userHandle,
          })}
        </Header>
        <div>
          {favorites.map(({ type, key }) => {
            let imageUrl;
            let title;
            let tmdbId: number | undefined;
            let releaseDate: string | null = null;
            let overview: string | null = null;
            let nominations: string[] = [];

            if (type === 'person') {
              // @ts-expect-error key is key of people
              let person = objectiveState.people[key];
              if (!person) return null;
              title = person.name;
              if (person.tmdb_data) {
                tmdbId = person.tmdb_data.tmdb_id;
                imageUrl = [
                  tmdbConfig.base_url,
                  tmdbConfig.poster_sizes.w185,
                  person.tmdb_data.tmdb_profile_path,
                ].join('');
              }
            } else {
              // @ts-expect-error key is key of films
              let film = objectiveState.films[key];
              if (!film) return null;
              title = film.title;
              nominations = nominationsByFilm.get(key) || [];

              if (film.tmdb_data) {
                tmdbId = film.tmdb_data.tmdb_id;
                releaseDate = film.tmdb_data.tmdb_release_date || null;
                overview = film.tmdb_data.tmdb_overview || null;
                imageUrl = [
                  tmdbConfig.base_url,
                  tmdbConfig.poster_sizes.w185,
                  film.tmdb_data.tmdb_poster_path,
                ].join('');
              }
            }

            let letterboxdHref =
              tmdbId && type === 'film'
                ? `https://letterboxd.com/tmdb/${tmdbId}/`
                : undefined;

            let letterboxdData =
              tmdbId && oscarFilmsStatus
                ? oscarFilmsStatus[String(tmdbId)]
                : null;

            let nominationsText =
              nominations.length > 0
                ? `${nominations.length} nomination${nominations.length > 1 ? 's' : ''}`
                : '';

            return (
              <div
                key={key}
                className="flex items-start gap-3 border-b border-neutral-100 py-3 last:border-b-0"
              >
                <div className="h-24 w-16 flex-none overflow-hidden rounded">
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={title}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-neutral-100 p-1 text-center text-[8px] text-neutral-400">
                      {title}
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    {letterboxdHref ? (
                      <a
                        href={letterboxdHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="truncate font-medium"
                      >
                        {title}
                      </a>
                    ) : (
                      <div className="truncate font-medium">{title}</div>
                    )}
                    {letterboxdHref && (
                      <ArrowUpRightIcon className="h-3 w-3 flex-none text-neutral-400" />
                    )}
                  </div>
                  {letterboxdData &&
                    (letterboxdData.rating !== null || letterboxdData.liked) && (
                      <div className="-mt-0.5 mb-0.5 flex items-center gap-2">
                        {letterboxdData.rating !== null && (
                          <StarRating
                            rating={letterboxdData.rating}
                            starClassName="text-rose-dark"
                            hideEmptyStars
                          />
                        )}
                        {letterboxdData.liked && (
                          <HeartIcon className="h-4 w-4 text-rose-default" />
                        )}
                      </div>
                    )}
                  {type === 'film' && (releaseDate || nominationsText) && (
                    <div className="mb-0.5 flex items-center gap-1 text-xs text-neutral-500">
                      {releaseDate && <span>Released {formatReleaseDate(releaseDate)}</span>}
                      {releaseDate && nominationsText && <span>•</span>}
                      {nominationsText && (
                        <Tooltip
                          text={nominations.join('\n')}
                          tooltipSide="bottom"
                          className="cursor-pointer"
                        >
                          <span className="underline decoration-dotted">
                            {nominationsText}
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  )}
                  {overview && (
                    <div className="text-sm leading-tight text-neutral-500">
                      <FilmOverview
                        overview={overview}
                        shortenTruncation={Boolean(
                          letterboxdData &&
                            (letterboxdData.rating !== null ||
                              letterboxdData.liked),
                        )}
                      />
                    </div>
                  )}
                  {isCurrentUser && (
                    <Action
                      disablePadding
                      className="mt-1"
                      onClick={() => {
                        if (favorites === null) return;
                        if (
                          !window.confirm(
                            copy['areYouSureYouWantToRemoveThisFavorite?'],
                          )
                        )
                          return;

                        toggleUserFavoriteMutation({
                          userId,
                          favorites,
                          favorite: { type, key },
                        });
                      }}
                    >
                      {copy.Remove}
                    </Action>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </>
    );
  };

export let FavoritesModal = ({
  isOpen,
  onClose,
  userId,
  userHandle,
  className,
}: {
  userId: string;
  userHandle: string;
  className?: string;
  isOpen: boolean;
  onClose: () => void;
}) => (
  <Modal isOpen={isOpen} onClose={onClose} className={className}>
    <Suspense fallback={<Loader />}>
      <FavoritesModalContent onClose={onClose} userId={userId} userHandle={userHandle} className={className} />
    </Suspense>
  </Modal>
);
